import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Injector,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, throttleTime } from 'rxjs';

import { AppShellService } from '@spaces-ui/app-shell';
import { BrowserStorageService } from '@spaces-ui/browser-storage';
import { ElectronServiceBus } from '@spaces-ui/electron-service-bus';
import { MdwButton } from '@spaces-ui/meadow/components/button';
import { Chevron_Left, Chevron_Right, Pin_Fill } from '@spaces-ui/meadow/icons/svgs';
import { ServiceBus } from '@spaces-ui/service-bus';
import { EventUtils } from '@spaces-ui/utils';

import { AsideBusEventPayloads, AsideBusEvents } from './aside-bus-events';

@UntilDestroy()
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'aside',
  imports: [CommonModule, MdwButton],
  templateUrl: './aside.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './aside.scss',
})
export class MdwAside implements OnInit {
  public element = inject<ElementRef<HTMLElement>>(ElementRef<HTMLElement>);
  public renderer = inject(Renderer2);
  public viewContainerRef = inject(ViewContainerRef);
  public injector = inject(Injector);
  public serviceBus = inject(ServiceBus);
  public electronServiceBus = inject(ElectronServiceBus);
  private appShellService = inject(AppShellService);
  private destroyRef = inject(DestroyRef);

  public id = input.required<string>();
  public isCollapsed = input<boolean | undefined>(undefined);
  public isFloating = input<boolean | undefined>(undefined);
  public width = model(312);
  public pinnedSurfaceStyle = input<'muted' | 'flat' | 'flat-inverse'>();
  public anchor = input<'left' | 'right'>('left');
  public floatAt = input(0);
  public autoCollapse = input(1024);
  public collapseWidth = input(0);
  public collapseTemplate = input<TemplateRef<HTMLElement>>();
  public allowResize = input<boolean>(false);
  public showResizeCollapseButton = input<boolean>(false);
  public floatCollapsedContent = input<boolean>(false);
  public isPrimarySidebar = input(false);
  public minWidth = input(264);
  public maxWidth = input(640);

  public clickedOutside = output();
  public showCollapsedContentsChange = output<boolean>();
  public isCollapsedChange = output<boolean>();
  public isFloatingChange = output<boolean>();

  public hostWidth = signal(0);
  public showingCollapsedContent = signal(false);
  public isPinning = signal(false);
  public anchorRightPx = signal(0);
  public anchorLeftPx = signal(0);
  public anchorTopPx = signal(0);
  public anchorBottomPx = signal(0);
  public disableAnimation = true;
  public isResizing = signal(false);
  public icons = { Pin_Fill };

  public isWindowsOrLinuxElectron = computed(() => {
    const isWindows = this.electronServiceBus.isWindowsElectronApp();
    const isLinux = this.electronServiceBus.isLinuxElectronApp();

    return isWindows || isLinux;
  });

  public isAutoCollapedOrAutoFloated = computed(() => {
    const hasAutoCollapsed = this.hasAutoCollapsed();
    const isAutoFloating = this.isAutoFloating();

    return hasAutoCollapsed || isAutoFloating;
  });

  private resizeSubject: Subject<void> = new Subject<void>();
  private lastCheckedWidth = 0;
  private leftArrowButtonContainer?: HTMLElement;
  private rightArrowButtonContainer?: HTMLElement;
  private resizeContainer?: HTMLElement;
  private ignoreMouseOut = false;
  private hasAutoCollapsed = signal(false);
  private isAutoFloating = signal(false);
  private collapseTimer?: number;
  private parentResizeObserver?: ResizeObserver;

  public constructor() {
    this.serviceBus
      .on<AsideBusEvents.IgnoreMouseOut, AsideBusEventPayloads>(AsideBusEvents.IgnoreMouseOut)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.ignoreMouseOut = true;
      });

    this.serviceBus
      .on<AsideBusEvents.ListenForMouseOut, AsideBusEventPayloads>(AsideBusEvents.ListenForMouseOut)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.ignoreMouseOut = false;
      });

    this.destroyRef.onDestroy(() => {
      if (this.parentResizeObserver) {
        this.parentResizeObserver.disconnect();
      }
    });

    effect(() => {
      const isFloating = this.isFloating();
      const showingCollapsedContent = this.showingCollapsedContent();

      this.showCollapsedContentsChange.emit(showingCollapsedContent || !!isFloating);

      if (isFloating) {
        this.renderer.addClass(this.element.nativeElement, 'floating');
      } else {
        this.renderer.removeClass(this.element.nativeElement, 'floating');
      }
    });

    effect(() => {
      const id = this.id();

      const storedWidth = BrowserStorageService.getLocalStorageItem(`${id}-width`);
      if (storedWidth) {
        setTimeout(() => this.width.set(parseInt(storedWidth)));
      }
    });

    effect(() => {
      const isCollapsed = this.isCollapsed();
      const allowResize = this.allowResize();

      if (allowResize) {
        this.updateCollapseButtons(!!isCollapsed);
      }
    });

    this.resizeSubject
      .pipe(throttleTime(150, undefined, { leading: true, trailing: true }))
      .subscribe(() => {
        this.evalCollapsedAndFloatingState();
      });
  }

  @HostListener('window:click', ['$event'])
  public onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // close sidebar if it's open and user clicks outside of it
    if (
      this.isCollapsed() ||
      !this.isFloating() ||
      // a detached target means the click destroyed its own UI (e.g. a
      // triggered CDK menu item) — it can't be judged inside or outside
      !target.isConnected ||
      target.closest('.mdw-aside-trigger') !== null ||
      target.closest('.collapse-button-container') !== null ||
      target.closest('mdw-emoji-panel') !== null ||
      target.closest('mdw-dialog') !== null ||
      target.closest('.cdk-overlay-container') !== null
    ) {
      return;
    }

    if (target.closest('aside') === null) {
      this.isCollapsedChange.emit(true);
      this.clickedOutside.emit();
    }
  }

  @HostListener('window:resize')
  public onResize() {
    this.resizeSubject.next();
  }

  public ngOnInit(): void {
    if (this.allowResize()) {
      this.insertResizeElements();
    }

    const parentElement = this.element.nativeElement.parentElement;
    if (parentElement && typeof ResizeObserver !== 'undefined') {
      this.parentResizeObserver = new ResizeObserver(() => this.resizeSubject.next());
      this.parentResizeObserver.observe(parentElement);
    }

    this.evalCollapsedAndFloatingState(true);
    this.element.nativeElement.classList.add(`anchor-${this.anchor()}`);
    this.disableAnimation = false;
  }

  public mouseEnter() {
    const isCollapsed = this.isCollapsed();
    const floatCollapsedContent = this.floatCollapsedContent();
    const isPrimarySidebar = this.isPrimarySidebar();

    if (isCollapsed && floatCollapsedContent) {
      this.showingCollapsedContent.set(true);

      if (isPrimarySidebar) {
        this.appShellService.setIsHoveringOverSidebar(true);
      }

      if (this.collapseTimer) {
        clearTimeout(this.collapseTimer);
        this.collapseTimer = undefined;
      }
    }
  }

  public mouseLeave() {
    const isCollapsed = this.isCollapsed();
    const floatCollapsedContent = this.floatCollapsedContent();
    const isPrimarySidebar = this.isPrimarySidebar();

    if (isCollapsed && floatCollapsedContent && !this.ignoreMouseOut) {
      this.appShellService.setIsHoveringOverSidebar(false);

      this.collapseTimer = setTimeout(() => {
        const isHovering = isPrimarySidebar
          ? this.appShellService.isHoveringOverSidebarOrHistory()
          : false;

        if (!isHovering) {
          this.showingCollapsedContent.set(false);
        }
      }, 150);
    }
  }

  public pinSidebar() {
    this.appShellService.setLeftBarPinning(true);
    this.isPinning.set(true);
    this.showingCollapsedContent.set(false);
    this.isCollapsedChange.emit(false);

    setTimeout(() => {
      this.isPinning.set(false);
      this.appShellService.setLeftBarPinning(false);
    }, 200);
  }

  private evalCollapsedAndFloatingState(isInitital = false) {
    const parentElement = this.element.nativeElement.parentElement;
    const parentWidth = parentElement?.clientWidth || window.innerWidth;
    const shouldAutoFloat = parentWidth < this.floatAt();
    const shouldAutoCollapse = !shouldAutoFloat && parentWidth < this.autoCollapse();
    this.isAutoFloating.set(shouldAutoFloat);

    if (parentWidth !== window.innerWidth && parentElement) {
      // figure out anchor right position
      const right = window.innerWidth - (parentElement.offsetLeft + parentElement.clientWidth);
      this.anchorRightPx.set(right);

      // figure out anchor left position
      const left = parentElement.offsetLeft;
      this.anchorLeftPx.set(left);

      const bBox = parentElement.getBoundingClientRect();

      // figure out anchor top position
      const top = bBox.top;
      this.anchorTopPx.set(top);

      // figure out anchor bottom position
      const bottom = window.innerHeight - (bBox.top + bBox.height);
      this.anchorBottomPx.set(bottom);
    } else {
      this.anchorRightPx.set(0);
      this.anchorLeftPx.set(0);
      this.anchorTopPx.set(0);
      this.anchorBottomPx.set(0);
    }

    const isCollapsed = this.isCollapsed();

    if (isInitital) {
      this.hasAutoCollapsed.set(shouldAutoCollapse);
      if (shouldAutoCollapse) {
        this.isCollapsedChange.emit(shouldAutoCollapse);
        this.isFloatingChange.emit(shouldAutoCollapse);
      } else if (shouldAutoFloat) {
        this.isFloatingChange.emit(shouldAutoFloat);
      }
      this.lastCheckedWidth = parentWidth;
    } else {
      // Only change the state if we just crossed the threshold
      const wasLastWidthAutoFloat = this.lastCheckedWidth < this.floatAt();
      const wasLastWidthAutoCollapsed =
        !wasLastWidthAutoFloat && this.lastCheckedWidth < this.autoCollapse();
      this.lastCheckedWidth = parentWidth;

      if (shouldAutoCollapse !== wasLastWidthAutoCollapsed) {
        if (shouldAutoCollapse && !isCollapsed) {
          this.hasAutoCollapsed.set(true);
          this.isCollapsedChange.emit(true);
          setTimeout(() => {
            this.isFloatingChange.emit(true);
          }, 200);
        } else if (!shouldAutoCollapse && this.hasAutoCollapsed()) {
          this.hasAutoCollapsed.set(false);
          this.isFloatingChange.emit(false);
          setTimeout(() => {
            this.isCollapsedChange.emit(false);
          }, 0);
        }
      } else if (shouldAutoFloat !== wasLastWidthAutoFloat) {
        this.isFloatingChange.emit(shouldAutoFloat);
      }
    }
  }

  private insertResizeElements() {
    const isLeft = this.anchor() === 'left';
    const isCollapsed = this.isCollapsed();
    const showCollapseButton = this.showResizeCollapseButton();
    const insertBeforeElement = isLeft
      ? (this.renderer.nextSibling(this.element.nativeElement) as HTMLElement)
      : this.element.nativeElement;
    const parent = this.element.nativeElement.parentElement as HTMLElement;

    // Create the container div
    this.resizeContainer = this.renderer.createElement('div');
    this.renderer.addClass(this.resizeContainer, 'resize-container');
    if (isCollapsed) {
      this.renderer.addClass(this.resizeContainer, 'collapsed');
    }

    // Create the resize bar
    const resizeBar = this.renderer.createElement('div');
    this.renderer.addClass(resizeBar, 'resize-bar');
    this.renderer.appendChild(this.resizeContainer, resizeBar);

    let startX = 0;
    let startWidth = 0;

    const resize = (event: MouseEvent) => {
      EventUtils.preventBubble(event);
      const isResizing = this.isResizing();
      if (!isResizing) {
        return;
      }

      const newWidth = isLeft
        ? startWidth + (event.clientX - startX)
        : startWidth - (event.clientX - startX);

      const minWidth = this.minWidth();
      const maxWidth = this.maxWidth();

      const validWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

      this.width.set(validWidth);
    };

    const stopResize = (event: MouseEvent) => {
      EventUtils.preventBubble(event);
      this.isResizing.set(false);

      BrowserStorageService.setLocalStorageItem(`${this.id()}-width`, `${this.width()}`);
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
    };

    resizeBar.addEventListener('mousedown', (event: MouseEvent) => {
      EventUtils.preventBubble(event);
      this.isResizing.set(true);
      startWidth = this.element.nativeElement.clientWidth;
      startX = event.clientX;

      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', stopResize);
    });

    if (showCollapseButton) {
      const showLeftArrow = this.showLeftArrow(!!isCollapsed, isLeft);

      // Create the collapse button containers
      this.leftArrowButtonContainer = this.renderer.createElement('div');
      this.renderer.addClass(this.leftArrowButtonContainer, 'collapse-button-container');
      this.rightArrowButtonContainer = this.renderer.createElement('div');
      this.renderer.addClass(this.rightArrowButtonContainer, 'collapse-button-container');

      if (showLeftArrow) {
        this.renderer.addClass(this.rightArrowButtonContainer, 'hidden');
      } else {
        this.renderer.addClass(this.leftArrowButtonContainer, 'hidden');
      }

      // Dynamically create the MdwButton
      const leftArrowButtonRef = this.viewContainerRef.createComponent(MdwButton, {
        injector: this.injector,
      });
      leftArrowButtonRef.setInput('icon', Chevron_Left);

      const rightArrowButtonRef = this.viewContainerRef.createComponent(MdwButton, {
        injector: this.injector,
      });
      rightArrowButtonRef.setInput('icon', Chevron_Right);

      // Attach the dynamically created components to the DOM
      const leftArrowButtonElement = leftArrowButtonRef.location.nativeElement as HTMLElement;
      leftArrowButtonElement.classList.add('mdw-secondary');
      this.renderer.appendChild(this.leftArrowButtonContainer, leftArrowButtonElement);
      leftArrowButtonElement.addEventListener('click', () => {
        this.isCollapsedChange.emit(!this.isCollapsed());
      });
      const rightArrowButtonElement = rightArrowButtonRef.location.nativeElement;
      rightArrowButtonElement.classList.add('mdw-secondary');
      this.renderer.appendChild(this.rightArrowButtonContainer, rightArrowButtonElement);
      rightArrowButtonElement.addEventListener('click', () => {
        this.isCollapsedChange.emit(!this.isCollapsed());
      });

      // Append the collapseButtonContainers to resizeContainer
      this.renderer.appendChild(resizeBar, this.leftArrowButtonContainer);
      this.renderer.appendChild(resizeBar, this.rightArrowButtonContainer);
    }

    this.renderer.insertBefore(parent, this.resizeContainer, insertBeforeElement);
  }

  private updateCollapseButtons(isCollapsed: boolean) {
    const isLeft = this.anchor() === 'left';
    const showLeftArrow = this.showLeftArrow(isCollapsed, isLeft);

    if (!this.resizeContainer) {
      return;
    }

    if (isCollapsed) {
      this.renderer.addClass(this.resizeContainer, 'collapsed');
    } else {
      this.renderer.removeClass(this.resizeContainer, 'collapsed');
    }

    if (!this.leftArrowButtonContainer || !this.rightArrowButtonContainer) {
      return;
    }

    if (showLeftArrow) {
      setTimeout(() => this.renderer.removeClass(this.leftArrowButtonContainer, 'hidden'), 200);
      this.renderer.addClass(this.rightArrowButtonContainer, 'hidden');
    } else {
      this.renderer.addClass(this.leftArrowButtonContainer, 'hidden');
      setTimeout(() => this.renderer.removeClass(this.rightArrowButtonContainer, 'hidden'), 200);
    }
  }

  private showLeftArrow(isCollapsed: boolean, isLeft: boolean) {
    const showLeftArrow = isLeft ? !isCollapsed : !!isCollapsed;

    return showLeftArrow;
  }
}
